<template>
  <section class="about-container">
    
    <header class="status-header">
      <h2 class="glitch-text">{{ t('AboutMe.status_title') }}</h2>
      <p class="footnote">{{ t('AboutMe.footnote') }}</p>
      <div class="status-badges">
        <span class="badge">{{ t('AboutMe.badge_stage') }}</span>
        <span class="badge outline">{{ t('AboutMe.badge_parcours') }}</span>
      </div>
    </header>

    <div class="bio-section">
      <p v-html="t('AboutMe.bio')"></p>
    </div>

    <div class="philosophy-grid">
      <div class="card perso">
        <div class="card-header">
          <h3>{{ t('AboutMe.perso_title') }}</h3>
        </div>
        <p>{{ t('AboutMe.perso_desc') }}</p>
      </div>

      <div class="card pro">
        <div class="card-header">
          <h3>{{ t('AboutMe.pro_title') }}</h3>
        </div>
        <p v-html="t('AboutMe.pro_desc')"></p>
      </div>
    </div>

    <div class="actions">
      <a :href="cvLink" target="_blank" class="btn-cv">
        <span class="btn-text">{{ t('AboutMe.btn_cv') }}</span>
      </a>
    </div>

  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

// Calcul du lien du CV en fonction de la langue actuelle
const cvLink = computed(() => {
  if (locale.value === 'en') {
    // Version Anglaise (Assurez-vous que ce fichier existe dans public/)
    return '/CV_Quentin_Belhadj_EN.pdf'
  } else {
    // Version Française par défaut
    return '/CV_Quentin_Belhadj.pdf'
  }
})
</script>

<style scoped>
/* --- CONFIGURATION GLOBALE --- */
.about-container {
  max-width: 900px;
  margin: 60px auto 80px;
  padding: 0 20px;
  font-family: 'Space Mono', 'Courier New', monospace; /* Police Tech */
  color: #050505; /* Texte principal noir par défaut sur le fond blanc */
}

/* --- EN-TÊTE --- */
.status-header {
  text-align: center;
  margin-bottom: 40px;
  position: relative;
}

.status-header::after {
  content: '';
  display: block;
  width: 60px;
  height: 3px;
  background: #000; /* Trait noir */
  margin: 25px auto 0;
}

h2 {
  font-size: 1.8rem;
  color: #000;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 700;
}

.status-badges {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.badge {
  background: #000;
  color: #fff;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: bold;
  text-transform: uppercase;
  border: 1px solid #000;
  border-radius: 4px;
}

.badge.outline {
  background: transparent;
  color: #000;
  border: 1px solid #555;
}

/* --- BIO --- */
.bio-section {
  text-align: center;
  max-width: 750px;
  margin: 0 auto 60px auto;
  line-height: 1.8;
  font-size: 1.25rem;
  color: #444; /* Gris foncé pour le texte courant */
}

.bio-section strong {
  color: #000; /* Noir pur pour l'emphase */
  font-weight: 700;
  border-bottom: 2px solid #ddd; /* Soulignement discret */
}

.asterisk {
  color: #000;
  vertical-align: super;
  font-size: 0.7em;
  margin-left: 2px;
  font-weight: bold;
}

.footnote {
  margin-top: 15px;
  font-size: 1rem;
  font-style: italic;
  color: #888;
}

.philosophy-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 60px;
}

.card {
  /* Style sombre "Tech" demandé */
  background: #0a0a0a; 
  color: #e0e0e0;
  padding: 35px;
  border-radius: 6px;
  border: 1px solid #333;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  display: flex;
  flex-direction: column;
}

/* Effet Hover : Légère élévation + Bordure blanche */
.card:hover {
  transform: translateY(-5px);
  border-color: #fff;
  box-shadow: 0 15px 30px rgba(0,0,0,0.15);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  border-bottom: 1px solid #333;
  padding-bottom: 15px;
}

.icon {
  font-size: 1.4rem;
  filter: grayscale(100%); /* Emojis en gris/noir */
}

h3 {
  margin: 0;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #fff;
}

.card p {
  line-height: 1.7;
  color: #fbfbfb;
  font-size: 0.95rem;
  margin: 0;
}

.card strong {
  color: #fff;
  border-bottom: 1px dotted #fff;
}

/* --- BOUTON CV (Animation "Slide") --- */
.actions {
  text-align: center;
}

.btn-cv {
  position: relative;
  display: inline-block;
  padding: 16px 45px;
  background: #000; /* Noir par défaut */
  color: #fff;      /* Texte blanc */
  text-decoration: none;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: bold;
  border: 2px solid #000;
  overflow: hidden; /* Important pour l'animation */
  transition: color 0.4s ease; /* Transition de la couleur du texte */
  cursor: pointer;
  z-index: 1;
}

/* L'élément qui va glisser (le fond blanc) */
.btn-cv::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 0%; /* Commence invisible (largeur 0) */
  height: 100%;
  background: #fff; /* Fond blanc au survol */
  transition: width 0.4s cubic-bezier(0.25, 0.8, 0.25, 1); /* Animation fluide */
  z-index: -1; /* Reste derrière le texte */
}

/* Au survol */
.btn-cv:hover {
  color: #000; /* Le texte devient noir pour être lisible sur le fond blanc */
}

.btn-cv:hover::before {
  width: 100%; /* Le fond blanc remplit tout le bouton */
}

/* --- RESPONSIVE --- */
@media (max-width: 768px) {
  .philosophy-grid {
    grid-template-columns: 1fr;
  }
  
  .desktop-break {
    display: none;
  }
  
  .bio-section {
    font-size: 1rem;
    padding: 0 10px;
  }
}
</style>